import type { ChapterId, ChapterStatus } from "./types";

const PREFIX = "vmc.tour.";
// Bumped to 2 for the tour's revision 2: a reviewer who saw revision 1 is
// offered the re-voiced chapters (and the hands-on viewer chapter) once more.
const VERSION = 2;
const CHAPTER_IDS: readonly ChapterId[] = ["flights", "viewer", "frames", "offline"];
const STATUSES: readonly ChapterStatus[] = ["completed", "skipped"];

interface Record2 {
  v: 2;
  chapters: Partial<Record<ChapterId, ChapterStatus>>;
  /** The chapter armed to start once on its page's next visit, whatever its
   *  status: set when a chapter ends with Done, cleared by Skip tour and when
   *  the armed chapter starts. This is what carries a hand-off ("open any
   *  flight and we'll pick it up there") across a page load. */
  next?: ChapterId;
}

/** Parse the stored record, or null when absent, malformed or another version. */
function readRecord(reviewerId: string | null): Record2 | null {
  try {
    const raw = localStorage.getItem(progressKey(reviewerId));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const rec = parsed as { v?: unknown; chapters?: unknown; next?: unknown };
    if (rec.v !== VERSION || typeof rec.chapters !== "object" || rec.chapters === null) {
      return null;
    }
    const chapters: Partial<Record<ChapterId, ChapterStatus>> = {};
    for (const [k, v] of Object.entries(rec.chapters as Record<string, unknown>)) {
      if (isChapterId(k) && isStatus(v)) chapters[k] = v;
    }
    const out: Record2 = { v: VERSION, chapters };
    if (isChapterId(rec.next)) out.next = rec.next;
    return out;
  } catch {
    return null;
  }
}

function writeRecord(reviewerId: string | null, rec: Record2): void {
  try {
    localStorage.setItem(progressKey(reviewerId), JSON.stringify(rec));
  } catch {
    // localStorage can be blocked (private mode, file://); the tour then
    // simply offers itself again next time.
  }
}

export function progressKey(reviewerId: string | null): string {
  return `${PREFIX}${reviewerId !== null && reviewerId !== "" ? reviewerId : "anon"}`;
}

function isChapterId(x: unknown): x is ChapterId {
  return typeof x === "string" && (CHAPTER_IDS as readonly string[]).includes(x);
}

function isStatus(x: unknown): x is ChapterStatus {
  return typeof x === "string" && (STATUSES as readonly string[]).includes(x);
}

export function readStatuses(
  reviewerId: string | null,
): Partial<Record<ChapterId, ChapterStatus>> {
  return readRecord(reviewerId)?.chapters ?? {};
}

export function readStatus(
  reviewerId: string | null,
  chapterId: ChapterId,
): ChapterStatus | undefined {
  return readStatuses(reviewerId)[chapterId];
}

export function writeStatus(
  reviewerId: string | null,
  chapterId: ChapterId,
  status: ChapterStatus,
): void {
  const prev = readRecord(reviewerId);
  const rec: Record2 = {
    v: VERSION,
    chapters: { ...(prev?.chapters ?? {}), [chapterId]: status },
  };
  if (prev?.next !== undefined) rec.next = prev.next;
  writeRecord(reviewerId, rec);
}

/** The chapter armed to auto-start on its page's next visit, or null. */
export function readNext(reviewerId: string | null): ChapterId | null {
  return readRecord(reviewerId)?.next ?? null;
}

/** Arm (or, with null, disarm) the chapter to auto-start next. */
export function writeNext(reviewerId: string | null, next: ChapterId | null): void {
  const prev = readRecord(reviewerId);
  const rec: Record2 = { v: VERSION, chapters: prev?.chapters ?? {} };
  if (next !== null) rec.next = next;
  writeRecord(reviewerId, rec);
}

export function clearProgress(reviewerId: string | null): void {
  try {
    localStorage.removeItem(progressKey(reviewerId));
  } catch {
    // see writeStatus
  }
}
